import { useMemo, useState } from 'react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { Search } from 'lucide-react';
import { t } from '@/i18n';
import { getCarrier, attentionStates } from '@/data/arrivalsData';
import { LoadingSectionList } from './LoadingSectionList';
import { noShowRisk } from '@/lib/noShowRisk';
import { HelpTip } from '@/components/ui/help-tip';

const HOURS = Array.from({ length: 13 }, (_, i) => 6 + i); // 6–18 h
const GATE_CAPACITY_PER_HOUR = 4; // PST + CLS together, demo assumption

// Sections replace the open/loaded filters (#117); type is one select instead of a second "All".
const filters = ['all', 'risk'];
const highRisk = (l) => noShowRisk(l)?.level === 'high';

export const ExpectedLoadings = ({ loadings, onSelect }) => {
  const [filter, setFilter] = useState('all');
  const [type, setType] = useState('all');
  const [search, setSearch] = useState('');

  // Expected arrivals per hour (ETA if known, otherwise plan).
  const perHour = useMemo(() => HOURS.map(h => {
    const inHour = loadings.filter(l => {
      const ts = new Date(l.eta && l.state !== 'rescheduled' && l.state !== 'no-power' ? l.eta : l.plannedAt);
      return ts.getDate() === new Date().getDate() && ts.getHours() === h;
    });
    return { h, count: inHour.length, unknown: inHour.filter(l => attentionStates.includes(l.state)).length };
  }), [loadings]);
  const maxPerHour = Math.max(GATE_CAPACITY_PER_HOUR, ...perHour.map(p => p.count));

  const visible = useMemo(() => {
    const q = search.toLowerCase();
    return loadings
      .filter(l => filter !== 'risk' || highRisk(l))
      .filter(l => type === 'all' || l.type === type)
      .filter(l => !q || [l.plate, l.driver.name, getCarrier(l.carrierId).name, l.orderId, l.material, l.id]
        .some(v => v.toLowerCase().includes(q)));
  }, [loadings, filter, type, search]);

  const counts = (f) => (f === 'risk' ? loadings.filter(highRisk).length : loadings.length);

  return (
    <div className="space-y-4">
      {/* Expected arrivals per hour vs gate capacity — shows the 6–9 and 12–14 peaks without dictating slots */}
      <Card className="p-4" data-tour="arrivals-per-hour">
        <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
          <div>
            <div className="text-sm font-semibold flex items-center gap-1">{t('arrivals.perHour')} <HelpTip id="perHour" /></div>
            <div className="text-xs text-muted-foreground">{t('arrivals.perHourHint')}</div>
          </div>
          <div className="flex items-center gap-3 text-[11px] text-muted-foreground">
            <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-primary/70" />{t('arrivals.known')}</span>
            <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-red-400" />{t('arrivals.unknown')}</span>
            <span className="flex items-center gap-1"><span className="w-3 border-t border-dashed border-foreground/50" />{t('arrivals.gateCapacity')}</span>
          </div>
        </div>
        <div className="relative h-24 flex items-end gap-1.5">
          <div
            className="absolute left-0 right-0 border-t border-dashed border-foreground/40 pointer-events-none"
            style={{ bottom: `${(GATE_CAPACITY_PER_HOUR / maxPerHour) * 100}%` }}
          />
          {perHour.map(p => (
            <div key={p.h} className="flex-1 flex flex-col items-center gap-1 h-full justify-end">
              <div className="w-full flex flex-col justify-end rounded-t overflow-hidden" style={{ height: `${(p.count / maxPerHour) * 100}%` }}>
                <div className="bg-red-400" style={{ height: p.count ? `${(p.unknown / p.count) * 100}%` : 0 }} />
                <div className="bg-primary/70 flex-1" />
              </div>
            </div>
          ))}
        </div>
        <div className="flex gap-1.5 mt-1">
          {perHour.map(p => (
            <div key={p.h} className="flex-1 text-center text-[10px] text-muted-foreground tabular-nums">{p.h}</div>
          ))}
        </div>
      </Card>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2">
        <HelpTip id="filters" />
        {filters.map(f => (
          <Badge
            key={f}
            variant="outline"
            onClick={() => setFilter(f)}
            className={cn('cursor-pointer gap-1', filter === f && 'bg-primary text-primary-foreground border-primary')}
          >
            {t(`arrivals.filter.${f}`)} <span className="font-bold">{counts(f)}</span>
          </Badge>
        ))}
        <Select value={type} onValueChange={setType}>
          <SelectTrigger className="ml-auto h-8 w-auto gap-1 text-xs" aria-label={t('arrivals.typeLabel')}>
            <span className="text-muted-foreground">{t('arrivals.typeLabel')}:</span> <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {['all', 'silo', 'pallet'].map(ty => <SelectItem key={ty} value={ty}>{t(`arrivals.type.${ty}`)}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder={t('arrivals.searchPlaceholder')}
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="pl-9"
        />
      </div>

      {/* Loadings */}
      <div className="space-y-2" data-tour="arrivals-list">
        {visible.length === 0 && (
          <Card className="p-6 text-center text-sm text-muted-foreground">{t('arrivals.emptyFilter')}</Card>
        )}
        <LoadingSectionList loadings={visible} onSelect={onSelect} />
      </div>
    </div>
  );
};
